import React from 'react';
import styled, {css} from 'styled-components';
import i18next from 'i18next';

import {Tooltip} from '@famly/mf_data-display_tooltip';
import {Icon} from '@famly/mf_data-display_icon';
import {StatusPill} from '@famly/mf_data-display_status-pill';
import {
    type InvoiceSummaryPeriod,
    type InvoiceSummaryPayable,
    type InvoiceSummary as InvoiceSummaryData,
    type PriceQuality,
    groupPeriodsByPlan,
    isCustomPeriod,
    formatDateRange,
    formatMonth,
    switchPeriodKind,
} from 'web-app/react/simplified-us-billing-plan/helpers/invoice-summary';
import {Box} from '@famly/mf_layout_box';
import {Text} from '@famly/mf_data-display_text';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';
import {Stack} from '@famly/mf_layout_stack';
import {
    CurrencyFormatterProvider,
    useCurrencyFormatter,
} from 'web-app/react/bookings-finance/contexts/currency-formatter-context';
import {hasValue} from '@famly/stat_ts-utils_has-value';

type InvoiceSummaryProperties = {
    invoiceSummary: InvoiceSummaryData;
    siteId: string;
    enableProrate: boolean;
};

/** Component for the entire invoice summary. */
export const InvoiceSummary = (properties: InvoiceSummaryProperties) => {
    const movesPricingGroupElement = properties.invoiceSummary.some(period => period.changesPricingGroup) ? (
        <Text variant="body" textAlign="right">
            * {i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.moveToNewPricingGroup')}
        </Text>
    ) : null;
    const noMoreInvoices =
        properties.invoiceSummary.length > 0 &&
        properties.invoiceSummary[properties.invoiceSummary.length - 1].isLastInPlan;
    const noMoreInvoicesElement = noMoreInvoices ? (
        <tr>
            <TdLabelRow colSpan={2}>
                <Box color="n300" mt={5}>
                    <Text variant="body">
                        {i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.noMoreInvoices')}
                    </Text>
                </Box>
            </TdLabelRow>
        </tr>
    ) : null;
    const billingContinues = properties.invoiceSummary.length > 0 && !noMoreInvoices;
    const billingContinuesElement = billingContinues ? (
        <tr>
            <TdLabelRow colSpan={2}>
                <Box color="n300" mt={5}>
                    <Text variant="body">
                        {i18next.t('react.simplifiedUsBillingPlan.translations.json.summaryInfo')}
                    </Text>
                </Box>
            </TdLabelRow>
        </tr>
    ) : null;
    const periodGroups = groupPeriodsByPlan(properties.invoiceSummary);
    return (
        <Box mt={5}>
            <CurrencyFormatterProvider siteSetId={properties.siteId}>
                <Stack direction="column">
                    {movesPricingGroupElement}
                    <table data-e2e-class="summary-table">
                        {periodGroups.map((periodGroup, index) => (
                            <>
                                {hasValue(periodGroup.alreadyInvoicedGroup) ? (
                                    <InvoiceSummaryGroup
                                        key={index * 2}
                                        path={[index * 2]}
                                        group={periodGroup.alreadyInvoicedGroup}
                                        isHighlighted={false}
                                        isInitialScroll={periodGroup.isCurrentPlan}
                                        showDivider={index !== 0}
                                        isLast={index + 1 === periodGroups.length}
                                        enableProrate={properties.enableProrate}
                                    />
                                ) : null}
                                <InvoiceSummaryGroup
                                    key={index * 2 + 1}
                                    path={[index * 2 + 1]}
                                    group={periodGroup.group}
                                    isHighlighted={periodGroup.isCurrentPlan}
                                    isInitialScroll={
                                        periodGroup.isCurrentPlan && !hasValue(periodGroup.alreadyInvoicedGroup)
                                    }
                                    showDivider={index !== 0 || hasValue(periodGroup.alreadyInvoicedGroup)}
                                    isLast={index + 1 === periodGroups.length}
                                    enableProrate={properties.enableProrate}
                                />
                            </>
                        ))}
                        {noMoreInvoicesElement}
                        {billingContinuesElement}
                    </table>
                </Stack>
            </CurrencyFormatterProvider>
        </Box>
    );
};

type InvoiceSummaryGroupProperties = {
    group: InvoiceSummaryData;
    // Show a blue line along periods.
    isHighlighted: boolean;
    // Is initial scroll position (either current plan or the partially invoiced plan before).
    isInitialScroll: boolean;
    showDivider: boolean;
    isLast: boolean;
    path: number[];
    enableProrate: boolean;
};

/** Part of the invoice summary showing periods for the same plan. */
const InvoiceSummaryGroup = (properties: InvoiceSummaryGroupProperties) => {
    // Scroll to the current plan on the initial load.
    const currentPlanRef = React.useRef<HTMLTableRowElement>(null);
    React.useLayoutEffect(() => {
        if (properties.isInitialScroll) {
            if (hasValue(currentPlanRef.current)) {
                currentPlanRef.current.scrollIntoView({behavior: 'instant', block: 'start'});
            }
        }
    }, [properties.isInitialScroll, currentPlanRef]);

    const planChangeText = i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.planChange');
    const divider = properties.showDivider ? (
        <tr>
            <TdDivider colSpan={2}>
                <Stack
                    width="100%"
                    position="absolute"
                    top={-12}
                    justifyContent="center"
                    ref={properties.isInitialScroll ? currentPlanRef : undefined}
                >
                    <StatusPill text={planChangeText} size="compact" severity="neutral" showDot={false} />
                </Stack>
            </TdDivider>
        </tr>
    ) : null;
    return (
        <>
            {divider}
            {properties.group.map((period, index) => (
                <InvoiceSummaryPeriod
                    key={index}
                    path={[...properties.path, index]}
                    period={period}
                    isHighlighted={properties.isHighlighted}
                    ref={index === 0 && !hasValue(divider) ? currentPlanRef : undefined}
                    enableProrate={properties.enableProrate}
                />
            ))}
        </>
    );
};

type InvoiceSummaryPeriodProperties = {
    period: InvoiceSummaryPeriod;
    isHighlighted: boolean;
    path: number[];
    enableProrate: boolean;
};

/** Component displaying a single invoice summary period. */
const InvoiceSummaryPeriod = React.forwardRef<HTMLTableRowElement, InvoiceSummaryPeriodProperties>(
    (properties, ref) => {
        const alreadyBilledElement = React.useMemo(() => {
            const alreadyBilledText = i18next.t(
                'react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.alreadyBilled',
            );
            const alreadyBilledTooltip = (
                <>
                    <Stack direction="column" color="n400">
                        <Text variant="micro" emphasized mb={2}>
                            {i18next.t(
                                'react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.alreadyInvoicedLine1',
                            )}
                        </Text>
                        <Text variant="micro" mb={1}>
                            {i18next.t(
                                'react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.alreadyInvoicedLine2',
                            )}
                        </Text>
                        <Ul>
                            <Li>
                                <Text variant="micro">
                                    {i18next.t(
                                        'react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.alreadyInvoicedLine3',
                                    )}
                                </Text>
                            </Li>
                            <Li>
                                <Text variant="micro">
                                    {i18next.t(
                                        'react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.alreadyInvoicedLine4',
                                    )}
                                </Text>
                            </Li>
                            <Li>
                                <Text variant="micro">
                                    {i18next.t(
                                        'react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.alreadyInvoicedLine5',
                                    )}
                                </Text>
                            </Li>
                        </Ul>
                    </Stack>
                </>
            );
            return properties.period.alreadyBilled ? (
                <Stack
                    direction="row"
                    gap={2}
                    display="inline-flex"
                    alignItems="center"
                    color={properties.isHighlighted ? 'n400' : 'n300'}
                >
                    <Tooltip placement="left" tooltipContent={alreadyBilledTooltip}>
                        <Icon name="help" />
                    </Tooltip>
                    <Text variant="body">{alreadyBilledText}</Text>
                </Stack>
            ) : null;
        }, [properties.isHighlighted, properties.period.alreadyBilled]);

        const dateElement = React.useMemo(
            () =>
                properties.period.changesPricingGroup ? (
                    <Tooltip
                        placement="top"
                        tooltipContent={i18next.t(
                            'react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.moveToNewPricingGroup',
                        )}
                    >
                        <Box>
                            <Text variant="body" emphasized={properties.isHighlighted} data-e2e-class="date">
                                {formatPeriodDate(properties.period)} *
                            </Text>
                        </Box>
                    </Tooltip>
                ) : (
                    <Text variant="body" emphasized={properties.isHighlighted} data-e2e-class="date">
                        {formatPeriodDate(properties.period)}
                    </Text>
                ),
            [properties.isHighlighted, properties.period],
        );

        return (
            <Tbody
                isHighlighted={properties.isHighlighted}
                data-e2e-class="summary-period"
                data-e2e-id={`summary-period-${showE2ePath(properties.path)}`}
            >
                <tr ref={ref}>
                    <TdPadding></TdPadding>
                    <TdPadding></TdPadding>
                </tr>
                <tr>
                    <TdDate>
                        <Stack
                            direction="row"
                            gap={2}
                            alignItems="center"
                            color={properties.isHighlighted ? 'n400' : 'n300'}
                        >
                            <Icon name="draft" />
                            {dateElement}
                        </Stack>
                    </TdDate>
                    <TdDate rightAlign>{alreadyBilledElement}</TdDate>
                </tr>
                {switchPeriodKind(
                    properties.period.kind,
                    () => (
                        <>
                            {properties.period.payables.map((payable, index) => (
                                <InvoiceSummaryPayable
                                    key={index}
                                    path={[...properties.path, index]}
                                    period={properties.period}
                                    payable={payable}
                                    isHighlighted={properties.isHighlighted}
                                    enableProrate={properties.enableProrate}
                                />
                            ))}
                        </>
                    ),
                    () => (
                        <InvoiceSummaryBreak path={properties.path} />
                    ),
                )}
                <tr>
                    <TdPadding></TdPadding>
                    <TdPadding></TdPadding>
                </tr>
            </Tbody>
        );
    },
);

type InvoiceSummaryPayableProperties = {
    period: InvoiceSummaryPeriod;
    payable: InvoiceSummaryPayable;
    isHighlighted: boolean;
    path: number[];
    enableProrate: boolean;
};

/** Component for showing a single tuition, fee, discount, total. */
const InvoiceSummaryPayable = (properties: InvoiceSummaryPayableProperties) => {
    const {formatAmount} = useCurrencyFormatter();
    const payableName = getPayableName(properties.payable);
    const priceString = formatPrice(properties.payable, amount => formatAmount({amount}), properties.enableProrate);
    const tooltipString = getPriceTooltip(properties.period.alreadyBilled, properties.payable.quality);
    const isEmphasized = properties.payable.kind === 'total';
    const priceElement = hasValue(tooltipString) ? (
        <Tooltip placement="left" tooltipContent={tooltipString}>
            <Box component="span">{priceString}</Box>
        </Tooltip>
    ) : (
        <>{priceString}</>
    );
    return (
        <tr data-e2e-id={`summary-payable-${showE2ePath(properties.path)}`}>
            <TdPayable>
                <Text
                    variant="body"
                    color={properties.isHighlighted ? 'n400' : 'n300'}
                    emphasized={isEmphasized}
                    data-e2e-class="summary-payable-name"
                >
                    {payableName}
                </Text>
            </TdPayable>
            <TdPayable rightAlign>
                <Text
                    variant="body"
                    color={properties.isHighlighted ? 'n400' : 'n300'}
                    emphasized={isEmphasized}
                    data-e2e-class="summary-payable-price"
                >
                    {priceElement}
                </Text>
            </TdPayable>
        </tr>
    );
};

type InvoiceSummaryBreakProperties = {
    path: number[];
};
const InvoiceSummaryBreak = (properties: InvoiceSummaryBreakProperties) => {
    const breakText = i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.onBreak');
    return (
        <tr data-e2e-id={`summary-break-${showE2ePath(properties.path)}`}>
            <TdPayable>
                <Text variant="body" color={'n300'} data-e2e-class="summary-break-name">
                    {breakText}
                </Text>
            </TdPayable>
        </tr>
    );
};

const getPayableName = (payable: InvoiceSummaryPayable): string => {
    switch (payable.kind) {
        case 'total':
            return i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.total');
        default:
            return payable.name;
    }
};

const formatPrice = (
    payable: InvoiceSummaryPayable,
    formatAmount: (amount: number) => string,
    enableProrate: boolean,
): string => {
    if (payable.quality === 'inaccurate' || (!enableProrate && payable.quality === 'prorated')) {
        return '—';
    }
    const approximateSymbol = getApproximateSymbol(payable.quality);
    const sign = payable.kind === 'discount' && payable.price > 0 ? '-' : '';
    const amountString = formatAmount(payable.price);
    return `${approximateSymbol} ${sign} ${amountString}`;
};

const getApproximateSymbol = (quality: PriceQuality): string => {
    switch (quality) {
        case 'exact':
            return '';
        case 'prorated':
            return '≈';
        case 'inaccurate':
            return '≠';
        default:
            exhaustiveCheck(quality);
            return '';
    }
};

// / Gets the tooltip to put on prices. Returns null if no tooltip should be shown.
const getPriceTooltip = (alreadyBilled: boolean, priceQuality: PriceQuality): string | null => {
    if (alreadyBilled) {
        return i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.alreadyInvoiced');
    }
    if (priceQuality === 'inaccurate' || priceQuality === 'prorated') {
        return i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummaryV2.calculatedAtInvoiceTime');
    }
    return null;
};

const Tbody = styled.tbody<{isHighlighted: boolean}>`
    border-left-width: 4px;
    border-top-width: 0;
    border-right-width: 0;
    border-bottom-width: 1px;
    border-bottom-color: ${({theme}) => theme.mf.colorPalette.n100};
    ${({isHighlighted}) =>
        isHighlighted
            ? css`
            background-color: ${({theme}) => theme.mf.colorPalette.n25};
            border-left-color: ${({theme}) => theme.mf.colorPalette.p300};
            data-highlighted;
        `
            : css`
                  background-color: ${({theme}) => theme.mf.colorPalette.n50};
                  border-left-color: ${({theme}) => theme.mf.colorPalette.n200};
              `}
`;

const TdDate = styled.td<{rightAlign?: boolean}>`
    padding-right: ${({theme}) => theme.mf.spacing(4)};
    padding-bottom: ${({theme}) => theme.mf.spacing(1)};
    padding-top: 0;
    cursor: default;
    ${({rightAlign}) =>
        rightAlign
            ? css`
                  text-align: right;
              `
            : ''};
`;

const TdPayable = styled.td<{rightAlign?: boolean}>`
    padding-left: ${({theme}) => theme.mf.spacing(4)};
    padding-right: ${({theme}) => theme.mf.spacing(4)};
    padding-top: ${({theme}) => theme.mf.spacing(1)};
    padding-bottom: 0;
    cursor: default;
    ${({rightAlign}) =>
        rightAlign
            ? css`
                  text-align: right;
              `
            : ''};
`;

const TdPadding = styled.td`
    padding-bottom: ${({theme}) => theme.mf.spacing(2)};
`;

const TdDivider = styled.td`
    position: relative;
    height: 0;
    padding: 0;
`;

const TdLabelRow = styled.td`
    text-align: center;
`;

const Ul = styled.ul`
    list-style-type: disc;
`;
const Li = styled.li`
    list-style-type: disc;
    margin-left: 1em;
`;

/** Converts a component path for e2e tags to a string. */
const showE2ePath = (path: number[]): string => path.map(step => step.toString()).join('-');

/** Formats a date range for a period as it should be shown in the invoice summary. */
const formatPeriodDate = (period: InvoiceSummaryPeriod) => {
    switch (period.billingFrequency) {
        case 'monthly':
            if (isCustomPeriod(period)) {
                return formatDateRange(period);
            } else {
                return formatMonth(period.periodDateRange.fromInclusive);
            }

        default:
            return formatDateRange(period);
    }
};
